import { Button, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import { Link as RemixLink } from "@remix-run/react";
import { FaFolder } from "react-icons/fa";
import { FiMenu } from "react-icons/fi";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import { useCallback, useEffect, useRef, useState } from "react";
import CategoryList from "../CategoryList";
import { categoryI } from "app/api/category/category.types";
import { toKebabCase } from "app/utils/fn";
import { categoryIcons } from "app/utils/categoryIcon";
import { useSafeIsMobile } from "app/utils/deviceDetection";

const CATEGORY_DROPDOWN_WIDTH = 360;
const CATEGORY_DROPDOWN_VIEWPORT_PAD = 16;

type CategoryHeaderBarProps = {
  categories: categoryI[];
};

export function CategoryHeaderBar({ categories }: CategoryHeaderBarProps) {
  const [isAllCategoriesOpen, setIsAllCategoriesOpen] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState<categoryI | null>(null);
  const [categoryDropdownAnchor, setCategoryDropdownAnchor] = useState<{
    left: number;
    top: number;
    width: number;
    dropdownLeft: number;
  } | null>(null);
  const categoryDropdownLeaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const categoryScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollCategoriesLeft, setCanScrollCategoriesLeft] = useState(false);
  const [canScrollCategoriesRight, setCanScrollCategoriesRight] = useState(true);
  const isMobileSafe = useSafeIsMobile();

  const checkCategoryScrollability = useCallback(() => {
    const el = categoryScrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollCategoriesLeft(scrollLeft > 0);
    setCanScrollCategoriesRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  const scrollCategories = useCallback((direction: "left" | "right") => {
    const el = categoryScrollRef.current;
    if (!el) return;
    const amount = 240;
    el.scrollTo({
      left: direction === "left" ? el.scrollLeft - amount : el.scrollLeft + amount,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    checkCategoryScrollability();
    const el = categoryScrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkCategoryScrollability);
    window.addEventListener("resize", checkCategoryScrollability);
    return () => {
      el.removeEventListener("scroll", checkCategoryScrollability);
      window.removeEventListener("resize", checkCategoryScrollability);
    };
  }, [categories, checkCategoryScrollability]);

  // Mobile UX: don't open subcategory dropdown on touch devices.
  useEffect(() => {
    if (!isMobileSafe) return;
    setHoveredCategory(null);
    setCategoryDropdownAnchor(null);
  }, [isMobileSafe]);

  if (categories.length === 0) return <div className="min-h-[3.37rem]"></div>;

  return (
    <div className="min-h-[3.37rem] flex w-full items-center gap-2 border-t border-gray-200 dark:border-gray-600 pt-3 mt-1 px-3 lg:px-0 min-w-0 relative">
      {/* Scroll left – visible on mobile only (pinned strip has its own on xl) */}
      {canScrollCategoriesLeft && (
        <button
          type="button"
          onClick={() => scrollCategories("left")}
          className="xl:hidden shrink-0 z-10 p-2 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          aria-label="Scroll categories left"
        >
          <IoChevronBack className="w-5 h-5" aria-hidden />
        </button>
      )}
      <div
        className="relative flex-1 min-w-0 flex items-stretch overflow-hidden"
        style={{ minHeight: 40 }}
      >
        {/* Pinned "All categories" – xl only; scroll left inside on xl */}
        <div className="hidden xl:flex absolute left-0 top-0 bottom-0 z-10 items-center gap-1 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-600 pr-2 w-[11rem]">
          <Popover
            showArrow
            placement="bottom-start"
            isOpen={isAllCategoriesOpen}
            onOpenChange={setIsAllCategoriesOpen}
          >
            <PopoverTrigger>
              <Button
                variant="flat"
                color="primary"
                aria-label="All product categories"
                aria-expanded={isAllCategoriesOpen}
                className={`${
                  isAllCategoriesOpen ? "bg-primary text-white" : ""
                } w-fit min-w-0 flex items-center gap-1.5 xl:gap-2  px-2 xl:px-3 py-2 font-semibold rounded-lg text-xs xl:text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2`}
              >
                <FiMenu className="text-base xl:text-lg shrink-0" aria-hidden />
                <span className="truncate">All categories</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-1 dark:bg-gray-700 border-gray-200 dark:border-gray-600 max-w-[min(100vw-2rem,24rem)]">
              <div className="h-[30rem] min-h-[30rem] 2xl:h-[36rem] min-w-56 xl:min-w-72 2xl:min-w-80 overflow-y-auto">
                <CategoryList
                  categories={categories}
                  top={0}
                  isHome={true}
                  closePopover={() => setIsAllCategoriesOpen(false)}
                />
              </div>
            </PopoverContent>
          </Popover>
          {canScrollCategoriesLeft && (
            <button
              type="button"
              onClick={() => scrollCategories("left")}
              className="shrink-0 p-2 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              aria-label="Scroll categories left"
            >
              <IoChevronBack className="w-5 h-5" aria-hidden />
            </button>
          )}
        </div>
        <div
          ref={categoryScrollRef}
          className="flex-1 flex items-center min-w-0 xl:ml-[11rem] overflow-x-auto scroll-smooth"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
          onScroll={checkCategoryScrollability}
        >
          <ul
            className="flex w-full min-w-0 items-center gap-4 xl:gap-6 font-medium list-none pl-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            role="list"
            aria-label="Product categories"
          >
            {categories.map((category) => {
              const IconComponent = categoryIcons[category.name] || FaFolder;
              const isHovered = hoveredCategory?._id === category._id;
              const subCategories = category.subCategory ?? [];
              const hasSubs = subCategories.length > 0;
              return (
                <li
                  key={category._id}
                  className="shrink-0"
                  onMouseEnter={(e) => {
                    if (isMobileSafe) return;
                    if (categoryDropdownLeaveTimer.current) {
                      clearTimeout(categoryDropdownLeaveTimer.current);
                      categoryDropdownLeaveTimer.current = null;
                    }
                    setHoveredCategory(category);
                    if (hasSubs) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const preferredLeft =
                        rect.left + rect.width / 2 - CATEGORY_DROPDOWN_WIDTH / 2;
                      const dropdownLeft = Math.max(
                        CATEGORY_DROPDOWN_VIEWPORT_PAD,
                        Math.min(
                          preferredLeft,
                          window.innerWidth -
                            CATEGORY_DROPDOWN_WIDTH -
                            CATEGORY_DROPDOWN_VIEWPORT_PAD,
                        ),
                      );
                      setCategoryDropdownAnchor({
                        left: rect.left,
                        top: rect.bottom,
                        width: rect.width,
                        dropdownLeft,
                      });
                    }
                  }}
                  onMouseLeave={() => {
                    categoryDropdownLeaveTimer.current = setTimeout(() => {
                      setHoveredCategory(null);
                      setCategoryDropdownAnchor(null);
                      categoryDropdownLeaveTimer.current = null;
                    }, 80);
                  }}
                >
                  <RemixLink
                    to={`/category/${toKebabCase(category.name)}`}
                    prefetch="intent"
                    className={`inline-flex items-center gap-1.5 text-foreground text-sm 3xl:text-lg transition-colors hover:text-primary ${
                      isHovered ? "text-primary border-b-2 border-primary font-semibold" : ""
                    }`}
                    aria-label={`${category.name} category`}
                    aria-haspopup={hasSubs}
                    aria-expanded={isHovered}
                  >
                    <IconComponent
                      className="text-lg text-zinc-500 dark:text-gray-400 shrink-0"
                      aria-hidden
                    />
                    <span className="truncate max-w-[8rem] 2xl:max-w-none">{category.name}</span>
                  </RemixLink>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      {canScrollCategoriesRight && (
        <button
          type="button"
          onClick={() => scrollCategories("right")}
          className="shrink-0 z-10 p-2 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          aria-label="Scroll categories right"
        >
          <IoChevronForward className="w-5 h-5" aria-hidden />
        </button>
      )}
      {hoveredCategory &&
        categoryDropdownAnchor &&
        !isMobileSafe &&
        (hoveredCategory.subCategory?.length ?? 0) > 0 && (
          <div
            className="fixed z-[100] pt-2"
            style={{
              left: categoryDropdownAnchor.dropdownLeft,
              top: categoryDropdownAnchor.top,
            }}
            onMouseEnter={() => {
              if (categoryDropdownLeaveTimer.current) {
                clearTimeout(categoryDropdownLeaveTimer.current);
                categoryDropdownLeaveTimer.current = null;
              }
            }}
            onMouseLeave={() => {
              setHoveredCategory(null);
              setCategoryDropdownAnchor(null);
            }}
          >
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg py-4 px-5 min-w-[300px] xl:min-w-[360px] transition-opacity duration-150 ring-1 ring-black/5 dark:ring-white/5">
              <RemixLink
                to={`/category/${toKebabCase(hoveredCategory.name)}`}
                prefetch="intent"
                className="block text-base font-semibold text-foreground hover:text-primary mb-3 pb-2 border-b border-gray-200 dark:border-gray-600 transition-colors"
              >
                All {hoveredCategory.name}
              </RemixLink>
              <ul
                className="flex flex-col flex-wrap gap-0.5 max-h-[min(50vh,30rem)] overflow-y-auto overscroll-contain pr-1"
                role="list"
              >
                {(hoveredCategory.subCategory ?? []).map((sub) => (
                  <li key={sub._id}>
                    <RemixLink
                      to={`/category/${toKebabCase(hoveredCategory.name)}/${toKebabCase(sub.name)}`}
                      prefetch="intent"
                      className="block py-2 px-3 rounded-lg text-sm text-foreground hover:bg-primary/10 dark:hover:bg-primary/20 hover:text-primary transition-colors capitalize"
                    >
                      {sub.name}
                    </RemixLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
    </div>
  );
}
